/**
 * Local database: IndexedDB through Dexie. Everything the app knows lives here, on the device.
 * Schema changes are added as new `this.version(n)` blocks — never edit a released version.
 */
import Dexie, { type Table } from "dexie";
import type {
  Attachment,
  BalanceAnchor,
  BudgetLine,
  Category,
  CategoryGroup,
  CategoryImportRecord,
  Expense,
  ExpenseDraft,
  Extraction,
  Income,
  IncomeBudget,
  PaymentMethod,
  Setting,
  TransferLogEntry,
} from "@/domain/model";
import type { ExcelCopyRecord } from "@/domain/excel-copy";
import type { MerchantAlias } from "@/domain/merchant-recognition";
import { upgradeExpenseRecord } from "@/domain/record-upgrades";

export const DATABASE_NAME = "bilancio";
/** Bumped together with every new Dexie version block; written into backups and sync packages. */
export const SCHEMA_VERSION = 5;

export class BudgetDatabase extends Dexie {
  categoryGroups!: Table<CategoryGroup, string>;
  categories!: Table<Category, string>;
  categoryImports!: Table<CategoryImportRecord, string>;
  paymentMethods!: Table<PaymentMethod, string>;
  expenses!: Table<Expense, string>;
  incomes!: Table<Income, string>;
  budgets!: Table<BudgetLine, string>;
  incomeBudgets!: Table<IncomeBudget, string>;
  balanceAnchors!: Table<BalanceAnchor, string>;
  settings!: Table<Setting, string>;
  transfers!: Table<TransferLogEntry, string>;
  expenseDrafts!: Table<ExpenseDraft, string>;
  attachments!: Table<Attachment, string>;
  excelCopies!: Table<ExcelCopyRecord, string>;
  extractions!: Table<Extraction, string>;
  merchantAliases!: Table<MerchantAlias, string>;

  constructor(name: string = DATABASE_NAME) {
    super(name);
    this.version(1).stores({
      categoryGroups: "id, &excelLabel, sortOrder",
      categories: "id, &excelLabel, groupId, sortOrder",
      categoryImports: "id, importedAt",
      paymentMethods: "id, &name, sortOrder",
      expenses: "id, accountingMonth, purchaseDate, status, source, categoryId, updatedAt, sync.status",
      incomes: "id, accountingMonth, date, status, source, updatedAt, sync.status",
      budgets: "id, accountingMonth, categoryId, updatedAt",
      incomeBudgets: "id, accountingMonth, updatedAt",
      balanceAnchors: "id, effectiveMonth, updatedAt",
      settings: "key",
      transfers: "id, kind, createdAt",
    });
    // v2: duplicate review on expenses (+ index to list the ones awaiting review).
    this.version(2)
      .stores({
        expenses: "id, accountingMonth, purchaseDate, status, source, categoryId, updatedAt, sync.status, duplicate.status",
      })
      .upgrade((tx) =>
        tx
          .table("expenses")
          .toCollection()
          .modify((expense: Expense) => {
            Object.assign(expense, upgradeExpenseRecord(expense));
          }),
      );
    // v3: local image attachments (receipt photos, screenshots) and persisted expense drafts.
    this.version(3).stores({
      expenseDrafts: "id, updatedAt",
      attachments: "id, [ownerKind+ownerId], ownerId, sha256, createdAt",
    });
    // v4: rows pasted into the workbook with "Copia per Excel" (keyed by expense id).
    this.version(4).stores({
      excelCopies: "id, copiedAt",
    });
    // v5: on-device OCR results and merchant aliases learned from the user's corrections.
    this.version(5).stores({
      extractions: "id, attachmentId, createdAt",
      merchantAliases: "id, updatedAt",
    });
  }
}

/** Stores included in backups, in restore order (reference data before the ledgers). */
export const BACKUP_TABLES = [
  "categoryGroups",
  "categories",
  "categoryImports",
  "paymentMethods",
  "expenses",
  "expenseDrafts",
  "attachments",
  "excelCopies",
  "extractions",
  "merchantAliases",
  "incomes",
  "budgets",
  "incomeBudgets",
  "balanceAnchors",
  "settings",
  "transfers",
] as const satisfies readonly (keyof BudgetDatabase)[];
export type BackupTable = (typeof BACKUP_TABLES)[number];

let appDatabase: BudgetDatabase | undefined;

/** The app's database (singleton). Tests create their own instances with unique names. */
export function getDatabase(): BudgetDatabase {
  appDatabase ??= new BudgetDatabase();
  return appDatabase;
}

export function nowIso(): string {
  return new Date().toISOString();
}
